import { Button } from '@efexteam/components';
import { ColorsWarning900 } from '@efexteam/design-tokens';
import {
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ALLOWED_STATUS, {
  ALLOWED_STATUS_FORWARDS,
} from '@pages/transferDetails/constants/allowedStatus';
import { CURRENCY_IDS } from '@utils/formater';
import { useTranslation } from 'react-i18next';
import { Actions, Can, Subjects } from '@efexteam/permission-manager';
import ActionCardStyles from './ActionCard.style';
import { ActionCardProps, AmountDisplayProps } from './types/actionCard.type';

const AmountDisplay = ({ label, value, currency }: AmountDisplayProps) => (
  <Stack className={ActionCardStyles.detailsItem}>
    <Typography variant="caption" component="span" aria-label={String(label)}>
      {label}
    </Typography>
    <Typography variant="subtitle1" component="span">
      {value} {currency && CURRENCY_IDS[currency]}
    </Typography>
  </Stack>
);

const ActionCard = ({
  timeValues,
  handleActionModal,
  isLoading = false,
  transfer,
}: ActionCardProps) => {
  const { t } = useTranslation();

  const handleApprove = () => {
    handleActionModal('approve');
  };

  const handleReject = () => {
    handleActionModal('reject');
  };

  const actionTitle = t('trsfDtlActionTitle');
  const approveLabel = t('trsfDtlActionApprove');
  const rejectLabel = t('trsfDtlActionReject');

  if (isLoading) {
    return (
      <Card className={ActionCardStyles.wrapper}>
        <Skeleton variant="rectangular" height={100} />
      </Card>
    );
  }

  if (!transfer) {
    return (
      <Card className={ActionCardStyles.wrapper}>
        <CardContent>
          <Typography variant="h6">{actionTitle}</Typography>
        </CardContent>
      </Card>
    );
  }

  const renderRateLabelUpdated = () => {
    if (
      ALLOWED_STATUS.includes(transfer.status.name) &&
      timeValues.minutes <= 0 &&
      timeValues.seconds <= 0
    ) {
      return (
        <>
          {t('trsfDtlActionExchangeRate')}{' '}
          <span style={{ color: ColorsWarning900, fontWeight: 600 }}>
            {t('trsfRateUpdated')}
          </span>
        </>
      );
    }
    return t('trsfDtlActionExchangeRate');
  };

  const actionButtons = () => {
    if (ALLOWED_STATUS.includes(transfer.status.name)) {
      return (
        <Can action={Actions.UPDATE} subject={Subjects.PAYMENTS}>
          <Stack className={ActionCardStyles.buttons}>
            <Button
              appearance="primary"
              onClick={handleApprove}
              disabled={isLoading}
              aria-label={approveLabel}
            >
              {approveLabel}
            </Button>
            <Button
              appearance="secondary"
              onClick={handleReject}
              disabled={isLoading}
              aria-label={rejectLabel}
            >
              {rejectLabel}
            </Button>
          </Stack>
        </Can>
      );
    }
    if (ALLOWED_STATUS_FORWARDS.includes(transfer.status.name)) {
      return (
        <Stack className={ActionCardStyles.buttons}>
          <Button
            appearance="primary"
            className="!mt-5"
            disabled={
              !transfer.forwardRemainingInfo ||
              transfer.forwardRemainingInfo?.remainingPercentage <= 0
            }
            variant="contained"
            onClick={() => handleApprove()}
          >
            {t('pyfwdConfirm')}
          </Button>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Card
      className={ActionCardStyles.wrapper}
      role="region"
      aria-label={actionTitle}
    >
      <CardContent>
        <Grid container>
          <Grid item xs={12} md={9}>
            <Stack className={ActionCardStyles.detailsContainer}>
              <Typography variant="h6" component="h2">
                {actionTitle}
              </Typography>
              <Stack className={ActionCardStyles.details}>
                <AmountDisplay
                  label={t('trsfDtlActionOriginAmount')}
                  value={transfer?.amount || 0}
                  currency={transfer?.currencyId || ''}
                />
                <AmountDisplay
                  label={t('trsfDtlActionDestinationAmount')}
                  value={transfer.desAmount}
                  currency={transfer?.desCurrencyId || ''}
                />
                {transfer.rate?.value && (
                  <AmountDisplay
                    label={renderRateLabelUpdated()}
                    value={Number(transfer.rate.value)}
                  />
                )}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            {actionButtons()}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ActionCard;
